import { Component, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { Client } from 'src/app/shared/client';
import { Person } from 'src/app/shared/person';
import { ClientApiService } from 'src/app/services/client-api.service';
import { AgentExtractedIdentitySignals } from 'src/app/shared/agent-data';
import { phoneDigits, splitContactName } from 'src/app/shared/agent-contact-utils';

interface FieldDiff {
  field: string;
  label: string;
  currentValue: string | null;
  extractedValue: string | null;
  checked: boolean;
  isCompanyField: boolean;
}

/**
 * AG-22 — Offers a reviewable update to stale customer/contact info from extracted email signals.
 * Only shown when a MEDIUM-confidence agent match found a `customer_id` *and* extracted signals
 * from the email signature (Step 3 path). Companion to `agent-customer-quick-create` (AG-21b),
 * which handles the opposite case (no match found, offer to create).
 *
 * The manager reviews each field difference separately and opts in per field via checkbox.
 * Saves with existing `PUT` calls to `ClientApiService`, never creates new backend routes.
 * See `docs/PROMPT_agent-customer-update-offer.md` for the full design.
 */
@Component({
  selector: 'app-agent-customer-update-offer',
  templateUrl: './agent-customer-update-offer.component.html',
  styleUrls: ['./agent-customer-update-offer.component.scss'],
})
export class AgentCustomerUpdateOfferComponent implements OnChanges, OnDestroy {
  @Input() customerId: number | null = null;
  @Input() personId: number | null = null;
  @Input() extractedSignals: AgentExtractedIdentitySignals | null = null;

  private destroy$ = new Subject<void>();

  loading = false;
  loadError: string | null = null;
  saving = false;

  currentClient: Client | null = null;
  currentPerson: Person | null = null;

  diffs: FieldDiff[] = [];

  constructor(
    private cliApi: ClientApiService,
    private messageService: MessageService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId'] || changes['personId'] || changes['extractedSignals']) {
      this.refreshData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasCheckedDiffs(): boolean {
    return this.diffs.some(d => d.checked);
  }

  get hasCompanyDiffs(): boolean {
    return this.diffs.some(d => d.isCompanyField && d.checked);
  }

  get hasPersonDiffs(): boolean {
    return this.diffs.some(d => !d.isCompanyField && d.checked);
  }

  private refreshData(): void {
    this.diffs = [];
    this.loadError = null;
    this.currentClient = null;
    this.currentPerson = null;

    if (!this.customerId || !this.extractedSignals) {
      return;
    }

    this.loading = true;

    this.cliApi.getClientById(this.customerId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (clients) => {
        if (clients.length === 0) {
          this.loadError = 'Клієнт більше не існує в базі даних';
          this.loading = false;
          return;
        }
        this.currentClient = clients[0];
        this.computeDiffs();

        if (this.personId) {
          this.cliApi.getPersonById(this.personId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (persons) => {
              if (persons.length > 0) {
                this.currentPerson = persons[0];
              }
              this.computeDiffs();
              this.loading = false;
            },
            error: () => {
              this.computeDiffs();
              this.loading = false;
            },
          });
        } else {
          this.loading = false;
        }
      },
      error: () => {
        this.loadError = 'Помилка при завантаженні даних клієнта';
        this.loading = false;
      },
    });
  }

  private computeDiffs(): void {
    this.diffs = [];

    if (!this.currentClient || !this.extractedSignals) {
      return;
    }

    // Company name diff
    if (this.extractedSignals.companyName) {
      const current = this.currentClient.CLIENTSNAME || '';
      if (current !== this.extractedSignals.companyName) {
        this.diffs.push({
          field: 'companyName',
          label: 'Назва організації',
          currentValue: current,
          extractedValue: this.extractedSignals.companyName,
          checked: false,
          isCompanyField: true,
        });
      }
    }

    // Registration code diff
    if (this.extractedSignals.registrationCode) {
      const current = this.currentClient.CLIENTREGCODE || '';
      if (current !== this.extractedSignals.registrationCode) {
        this.diffs.push({
          field: 'registrationCode',
          label: 'Реєстраційний код',
          currentValue: current,
          extractedValue: this.extractedSignals.registrationCode,
          checked: false,
          isCompanyField: true,
        });
      }
    }

    // Person fields - only if personId is set and person was loaded
    if (this.personId && this.currentPerson) {
      // Phone diff (check all three phone fields)
      if (this.extractedSignals.phone) {
        const extractedDigits = phoneDigits(this.extractedSignals.phone);
        const currentPhones = [this.currentPerson.PERSONPHONE, this.currentPerson.PERSONCPHONE, this.currentPerson.PERSONPHONE1];
        const hasMatchingPhone = currentPhones.some(p => phoneDigits(p) === extractedDigits);

        if (!hasMatchingPhone) {
          this.diffs.push({
            field: 'phone',
            label: 'Телефон',
            currentValue: currentPhones.filter(p => p).join(' / ') || null,
            extractedValue: this.extractedSignals.phone,
            checked: false,
            isCompanyField: false,
          });
        }
      }

      // Contact name diffs
      const { fname: extractedFname, lname: extractedLname } = splitContactName(this.extractedSignals);
      if (extractedFname || extractedLname) {
        const currentFname = this.currentPerson.PERSONFNAME || '';
        const currentLname = this.currentPerson.PERSONLNAME || '';

        // First name diff (if extracted first name exists and differs)
        if (extractedFname && currentFname !== extractedFname) {
          this.diffs.push({
            field: 'contactFirstName',
            label: 'Ім\'я',
            currentValue: currentFname,
            extractedValue: extractedFname,
            checked: false,
            isCompanyField: false,
          });
        }

        // Last name diff (if extracted last name exists and differs)
        if (extractedLname && currentLname !== extractedLname) {
          this.diffs.push({
            field: 'contactLastName',
            label: 'Прізвище',
            currentValue: currentLname,
            extractedValue: extractedLname,
            checked: false,
            isCompanyField: false,
          });
        }
      }
    }
  }

  onSave(): void {
    if (!this.hasCheckedDiffs) {
      return;
    }

    this.saving = true;
    let completed = 0;
    const total = (this.hasCompanyDiffs ? 1 : 0) + (this.hasPersonDiffs ? 1 : 0);

    const onComplete = () => {
      completed++;
      if (completed === total) {
        this.saving = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Успіх',
          detail: 'Дані оновлено. Перезавантажуємо...',
        });
        // Re-fetch to show current state
        this.refreshData();
      }
    };

    const onError = () => {
      completed++;
      if (completed === total) {
        this.saving = false;
      }
    };

    if (this.hasCompanyDiffs) {
      this.saveClient(onComplete, onError);
    }

    if (this.hasPersonDiffs) {
      this.savePerson(onComplete, onError);
    }
  }

  private saveClient(onComplete: () => void, onError: () => void): void {
    if (!this.currentClient) {
      return;
    }

    const patchedClient = { ...this.currentClient };

    // Apply checked company fields
    for (const diff of this.diffs) {
      if (diff.checked && diff.isCompanyField && diff.extractedValue) {
        if (diff.field === 'companyName') {
          patchedClient.CLIENTSNAME = diff.extractedValue;
        } else if (diff.field === 'registrationCode') {
          patchedClient.CLIENTREGCODE = diff.extractedValue;
        }
      }
    }

    this.cliApi.putClientById(patchedClient).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        onComplete();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Помилка',
          detail: 'Не вдалося оновити організацію',
        });
        onError();
      },
    });
  }

  private savePerson(onComplete: () => void, onError: () => void): void {
    if (!this.currentPerson) {
      return;
    }

    const patchedPerson = { ...this.currentPerson };

    // Apply checked person fields
    for (const diff of this.diffs) {
      if (diff.checked && !diff.isCompanyField && diff.extractedValue) {
        if (diff.field === 'phone') {
          patchedPerson.PERSONPHONE = diff.extractedValue;
        } else if (diff.field === 'contactFirstName') {
          patchedPerson.PERSONFNAME = diff.extractedValue;
        } else if (diff.field === 'contactLastName') {
          patchedPerson.PERSONLNAME = diff.extractedValue;
        }
      }
    }

    this.cliApi.putPersonById(patchedPerson).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        onComplete();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Помилка',
          detail: 'Не вдалося оновити контакт',
        });
        onError();
      },
    });
  }
}
